/**
 * Teams / Check-in page search bar. Uses the reusable SearchBar and binds
 * it to parent-provided props (parent can wire Redux/tools or local state).
 * Filter content (file types) is passed as children of the search bar.
 */
import React from "react";
import { useSearchPanel } from "../../../theme/styles/SearchPanelStyle";
import { ONEDRIVE_FILE_TYPES } from "../../../redux/constants/metaData";
import wordIcon from "../../../assets/word.svg";
import excel from "../../../assets/excel.svg";
import pdf from "../../../assets/icons/pdf.png";
import powerpoint from "../../../assets/powerpoint.svg";
import SearchBar from "../../../components/common/SearchBar";

const TeamsSearchBar = ({
  searchValue,
  onSearchValueChange,
  onClearAndSearch,
  selectedFileType,
  onFileTypeChange,
  isFilterVisible,
  onFilterVisibleChange,
  onSearch,
  searchLoading = false,
  placeholder = "Search for documents",
  searchBarRef,
}) => {
  const searchPanelStyle = useSearchPanel();

  const filterButton = (fileType, label, icon) => {
    const isSelected = selectedFileType === fileType;
    const disabled = searchLoading;
    const containerStyle = {
      ...(isSelected
        ? searchPanelStyle.FileTypeButtonContainerSelectedStyle
        : searchPanelStyle.FileTypeButtonContainerStyle),
      ...(disabled && { pointerEvents: "none", opacity: 0.6 }),
    };
    return (
      <div
        key={fileType ?? "all"}
        style={containerStyle}
        onClick={() => !disabled && onFileTypeChange?.(fileType)}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => !disabled && e.key === "Enter" && onFileTypeChange?.(fileType)}
        aria-disabled={disabled}
      >
        <button
          type="button"
          disabled={disabled}
          onClick={() => !disabled && onFileTypeChange?.(fileType)}
          style={searchPanelStyle.FileTypeButtonStyle}
        >
          {icon}
          <span style={searchPanelStyle.FileTypeTextStyle}>{label}</span>
        </button>
      </div>
    );
  };

  const filterContent = (
    <>
      {filterButton(ONEDRIVE_FILE_TYPES.allFiles, "All Files", null)}
      {filterButton(
        ONEDRIVE_FILE_TYPES.docx,
        "Word",
        <img src={wordIcon} alt="Word" style={searchPanelStyle.IconImageStyle} />,
      )}
      {filterButton(
        ONEDRIVE_FILE_TYPES.xlsx,
        "Excel",
        <img src={excel} alt="Excel" style={searchPanelStyle.IconImageStyle} />,
      )}
      {filterButton(
        ONEDRIVE_FILE_TYPES.pptx,
        "PowerPoint",
        <img src={powerpoint} alt="PowerPoint" style={searchPanelStyle.IconImageStyle} />,
      )}
      {filterButton(
        ONEDRIVE_FILE_TYPES.pdf,
        "PDF",
        <img src={pdf} alt="PDF" style={searchPanelStyle.PdfFilterIconImageStyle} />,
      )}
    </>
  );

  return (
    <SearchBar
      ref={searchBarRef}
      value={searchValue}
      onChange={onSearchValueChange}
      onSearch={onSearch}
      onClearAndSearch={onClearAndSearch}
      placeholder={placeholder}
      disabled={false}
      loading={searchLoading}
      showFilterButton
      isFilterVisible={isFilterVisible}
      onFilterVisibleChange={onFilterVisibleChange}
      filterContent={filterContent}
    />
  );
};

export default TeamsSearchBar;
